import React, { useState, useRef } from 'react';
import { ArrowRight, Sparkles, Users, GraduationCap, Calendar, Mail, Phone, CheckCircle2, ShieldCheck, Wrench, Briefcase, Home, Shirt, Droplets, HeartHandshake } from 'lucide-react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
// Brand colors specific to ProMaison
const NAVY = '#1E3A5F';
const NAVY_DARK = '#152C47';
const GOLD = '#D4A574';
const GOLD_LIGHT = '#F5EBDD';
type Audience = 'personnel' | 'employeur' | null;
export function PromaisonPage() {
  const [audience, setAudience] = useState<Audience>(null);
  const parcoursRef = useRef<HTMLDivElement>(null);
  const selectAudience = (next: Audience) => {
    setAudience(next);
    // Smooth scroll to the parcours section
    setTimeout(() => {
      parcoursRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
  };
  return <Layout data-id="element-1602">
      <SEO title="PROMAISON par ADCC | Renforcement des capacités des personnels de maison" description="PROMAISON est le programme ADCC de renforcement des capacités des personnels de maison au Bénin. Deux parcours — Fondamental et Expert — pour répondre aux besoins exprimés par les employeurs. Session 20-30 juin 2026 à Cotonou." url="https://adcc.africa/promaison" data-id="element-1603" />

      {/* ─────────────────────────────────────────────── */}
      {/* HERO */}
      {/* ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1604">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1605">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, #D4A574 1px, transparent 1px), radial-gradient(circle at 80% 70%, #ffffff 1px, transparent 1px)',
          backgroundSize: '60px 60px, 80px 80px'
        }} data-id="element-1606" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32" data-id="element-1607">
          <div className="text-center max-w-4xl mx-auto" data-id="element-1608">
            {/* PROMAISON logo on a soft round platform that absorbs its white edges */}
            <div className="relative flex justify-center mb-10" data-id="element-1609">
              <div className="relative w-80 h-80 md:w-[28rem] md:h-[28rem] lg:w-[32rem] lg:h-[32rem] rounded-full flex items-center justify-center" style={{
              background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #ffffff 55%, rgba(255,255,255,0.6) 70%, rgba(255,255,255,0) 85%)'
            }} data-id="element-1610">
                <img src="https://cdn.magicpatterns.com/uploads/xq7xorXDBCdoHQja5rTP8f/PROMAISON_logo_extrait.png" alt="PROMAISON par ADCC" className="w-64 md:w-80 lg:w-[24rem] h-auto object-contain" data-id="element-1611" />
              </div>
            </div>

            <p className="text-xs md:text-sm font-medium tracking-[0.3em] uppercase mb-10" style={{
            color: GOLD
          }} data-id="element-1612">
              Association Dignité Compétence Création
            </p>

            <p className="text-3xl md:text-5xl font-light italic text-white mb-6 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1613">
              Votre maison mérite mieux.
            </p>
            <p className="text-base md:text-lg text-blue-100 max-w-2xl mx-auto leading-relaxed mb-12" data-id="element-1614">
              Programme de renforcement des capacités des personnels de maison
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1615">
              <button type="button" onClick={() => selectAudience('personnel')} className="inline-flex items-center justify-center gap-2 text-base px-8 py-4 rounded-md font-medium shadow-xl hover:shadow-2xl transition-all bg-white hover:bg-gray-50 cursor-pointer" style={{
              color: NAVY
            }} data-id="element-1616">
                <GraduationCap className="w-5 h-5" data-id="element-1617" />
                Je suis personnel de maison
                <ArrowRight className="w-5 h-5" data-id="element-1618" />
              </button>
              <button type="button" onClick={() => selectAudience('employeur')} className="inline-flex items-center justify-center gap-2 text-base px-8 py-4 rounded-md font-medium border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer" data-id="element-1619">
                <Briefcase className="w-5 h-5" data-id="element-1620" />
                Je suis employeur
                <ArrowRight className="w-5 h-5" data-id="element-1621" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* ENQUÊTE — chiffres clés */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24 bg-white" data-id="element-1622">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1623">
          <div className="mb-12" data-id="element-1624">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1625">
              Pourquoi PROMAISON ?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1626">
              Un programme né des besoins exprimés par les employeurs eux-mêmes.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1627">
              En février 2026, ADCC a réalisé une enquête auprès d'employeurs de
              personnels de maison à Cotonou. Les résultats ont mis en évidence
              des préoccupations récurrentes :
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-1628">
            {[{
            pct: '62,5%',
            desc: 'des employeurs évoquent des difficultés liées à la fiabilité et à la confiance'
          }, {
            pct: '56,3%',
            desc: 'signalent une méconnaissance des outils, produits et bonnes pratiques'
          }, {
            pct: '25%',
            desc: 'rencontrent des problèmes de ponctualité ou font face à un turn-over fréquent'
          }].map((stat, idx) => <div key={idx} className="rounded-xl p-7 border-l-4 transition-shadow hover:shadow-md" style={{
            backgroundColor: '#F1F5F9',
            borderColor: NAVY
          }} data-id="element-1629">
                <p className="text-5xl md:text-6xl font-black mb-3" style={{
              color: NAVY,
              fontFamily: "'Playfair Display', Georgia, serif"
            }} data-id="element-1630">
                  {stat.pct}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1631">
                  {stat.desc}
                </p>
              </div>)}
          </div>

          {/* Quote banner */}
          <div className="rounded-2xl p-8 md:p-10 text-center relative" style={{
          backgroundColor: NAVY_DARK
        }} data-id="element-1632">
            <span className="absolute top-4 left-6 text-5xl opacity-40 font-serif leading-none" style={{
            color: GOLD
          }} data-id="element-1633">
              «
            </span>
            <p className="text-lg md:text-xl text-white font-light italic leading-relaxed max-w-3xl mx-auto px-6" data-id="element-1634">
              PROMAISON n'est pas né d'une idée théorique. Le programme est né
              de besoins exprimés par les employeurs eux-mêmes.
            </p>
            <span className="absolute bottom-4 right-6 text-5xl opacity-40 font-serif leading-none" style={{
            color: GOLD
          }} data-id="element-1635">
              »
            </span>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* CHIFFRES — 41 / 5 / 10 / 1 */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24" style={{
      backgroundColor: NAVY
    }} data-id="element-1636">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1637">
          <div className="text-center mb-14" data-id="element-1638">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1639">
              PROMAISON en chiffres
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight max-w-3xl mx-auto" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1640">
              Depuis son lancement, PROMAISON accompagne concrètement les
              personnels de maison et leurs employeurs.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-1641">
            {[{
            num: '41',
            label: 'bénéficiaires déjà accompagnés'
          }, {
            num: '5',
            label: 'moniteurs formés par ADCC'
          }, {
            num: '10',
            label: 'jours de programme — théorie et pratique'
          }, {
            num: '1',
            label: "attestation de participation délivrée par ADCC à l'issue du parcours"
          }].map((stat, idx) => <div key={idx} className="rounded-xl p-6 md:p-8 text-center" style={{
            backgroundColor: NAVY_DARK
          }} data-id="element-1642">
                <p className="text-6xl md:text-7xl font-black mb-3" style={{
              color: GOLD,
              fontFamily: "'Playfair Display', Georgia, serif"
            }} data-id="element-1643">
                  {stat.num}
                </p>
                <p className="text-blue-200 text-sm leading-relaxed" data-id="element-1644">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* CONVICTION */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28" style={{
      backgroundColor: GOLD_LIGHT
    }} data-id="element-1645">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1646">
          <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
          color: GOLD
        }} data-id="element-1647">
            Notre conviction
          </span>
          <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-8" style={{
          color: NAVY,
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-1648">
            Compétence et emploi durable
          </h2>
          <div className="space-y-5" data-id="element-1649">
            <p className="text-base md:text-lg leading-relaxed" style={{
            color: NAVY_DARK
          }} data-id="element-1650">
              Chez ADCC, nous sommes convaincus que le renforcement des
              capacités constitue l'un des fondements de l'emploi durable.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{
            color: NAVY_DARK
          }} data-id="element-1651">
              En améliorant les pratiques des personnels de maison, PROMAISON
              contribue à renforcer la confiance entre employeurs et employés, à
              améliorer la qualité du service rendu aux familles et à favoriser
              des relations de travail plus stables dans la durée.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* PROGRAMME — 4 thèmes */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24 bg-white" data-id="element-1652">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1653">
          <div className="mb-14 max-w-3xl" data-id="element-1654">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1655">
              Le programme
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1656">
              Quatre thèmes fondamentaux, ancrés dans la réalité du terrain.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1657">
              PROMAISON combine apports théoriques, exercices pratiques et
              partage d'expériences, pour répondre aux situations concrètes
              rencontrées dans les foyers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1658">
            {[{
            icon: Home,
            title: 'Entretien du domicile',
            items: ['Organisation du travail', "Techniques d'entretien", 'Utilisation du matériel']
          }, {
            icon: Droplets,
            title: 'Hygiène et sécurité',
            items: ['Utilisation des produits', 'Prévention des risques', "Bonnes pratiques d'hygiène"]
          }, {
            icon: Shirt,
            title: 'Gestion du linge',
            items: ['Tri et entretien des textiles', 'Techniques de repassage', 'Préservation des vêtements']
          }, {
            icon: HeartHandshake,
            title: 'Relation professionnelle',
            items: ["Communication avec l'employeur", 'Discrétion et respect', 'Ponctualité et fiabilité']
          }].map((mod, idx) => <Card key={idx} className="border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-1659">
                <CardContent className="p-7" data-id="element-1660">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{
                backgroundColor: GOLD_LIGHT
              }} data-id="element-1661">
                    <mod.icon className="w-6 h-6" style={{
                  color: NAVY
                }} data-id="element-1662" />
                  </div>
                  <h3 className="font-bold text-lg mb-4" style={{
                color: NAVY
              }} data-id="element-1663">
                    {mod.title}
                  </h3>
                  <ul className="space-y-2" data-id="element-1664">
                    {mod.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed" data-id="element-1665">
                        <span style={{
                    color: GOLD
                  }} data-id="element-1666">
                          —
                        </span>
                        <span data-id="element-1667">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* PARCOURS — Fondamental vs Expert */}
      {/* ─────────────────────────────────────────────── */}
      <section ref={parcoursRef} className="py-20 md:py-24 bg-gray-50" data-id="element-1668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1669">
          <div className="mb-10 max-w-3xl" data-id="element-1670">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1671">
              Deux parcours
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1672">
              Un programme adapté à chaque situation.
            </h2>
            <p className="text-gray-600" data-id="element-1673">
              Choisissez votre profil pour découvrir le parcours qui vous
              correspond.
            </p>
          </div>

          {/* Audience filter */}
          <div className="flex flex-wrap gap-3 mb-10" data-id="element-1674">
            <button type="button" onClick={() => setAudience('personnel')} className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all ${audience === 'personnel' ? 'shadow-md text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'}`} style={audience === 'personnel' ? {
            backgroundColor: NAVY
          } : {}} data-id="element-1675">
              <GraduationCap className="w-4 h-4" data-id="element-1676" />
              Personnel de maison
            </button>
            <button type="button" onClick={() => setAudience('employeur')} className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all ${audience === 'employeur' ? 'shadow-md text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'}`} style={audience === 'employeur' ? {
            backgroundColor: NAVY
          } : {}} data-id="element-1677">
              <Briefcase className="w-4 h-4" data-id="element-1678" />
              Employeur
            </button>
            {audience !== null && <button type="button" onClick={() => setAudience(null)} className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors underline" data-id="element-1679">
                Voir les deux parcours
              </button>}
          </div>

          <div className={`grid grid-cols-1 gap-8 ${audience === null ? 'lg:grid-cols-2' : 'max-w-2xl mx-auto'}`} data-id="element-1680">
            {/* Fondamental — Personnel de maison */}
            {(audience === null || audience === 'personnel') && <div id="parcours-personnel" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow" data-id="element-1681">
                {/* Blue header */}
                <div className="p-8 md:p-10" style={{
              backgroundColor: '#3B6BA0'
            }} data-id="element-1682">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5 bg-white/20 text-white" data-id="element-1683">
                    Personnel de maison
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{
                fontFamily: "'Playfair Display', Georgia, serif"
              }} data-id="element-1684">
                    Parcours Fondamental
                  </h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-1685">
                    Pour les agents d'entretien et assistants(es) ménagers(ères)
                    souhaitant renforcer leurs capacités et améliorer leurs
                    pratiques.
                  </p>
                </div>

                {/* White content */}
                <div className="p-8 md:p-10" data-id="element-1686">
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg mb-6 text-sm" style={{
                backgroundColor: '#EEF4FB',
                color: NAVY
              }} data-id="element-1687">
                    <Calendar className="w-4 h-4 flex-shrink-0" data-id="element-1688" />
                    <span className="font-medium" data-id="element-1689">
                      10 jours — théorie et pratique en situation réelle
                    </span>
                  </div>

                  <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{
                color: NAVY_DARK
              }} data-id="element-1690">
                    Thèmes abordés
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1691">
                    {['Entretien du domicile', 'Hygiène et sécurité', 'Gestion du linge', 'Relation professionnelle'].map(theme => <span key={theme} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{
                  backgroundColor: '#EEF4FB',
                  color: NAVY
                }} data-id="element-1692">
                        {theme}
                      </span>)}
                  </div>

                  <div className="rounded-lg p-4 mb-7 border-l-4" style={{
                backgroundColor: '#EEF4FB',
                borderColor: NAVY
              }} data-id="element-1693">
                    <p className="text-sm flex items-start gap-2" style={{
                  color: NAVY
                }} data-id="element-1694">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1695" />
                      <span data-id="element-1696">
                        Attestation de participation PROMAISON délivrée par ADCC
                        à l'issue du parcours
                      </span>
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200 text-center" data-id="element-1697">
                    <p className="text-xs tracking-wide mb-2" style={{
                  color: GOLD
                }} data-id="element-1698">
                      Participation aux frais d'encadrement pédagogique
                    </p>
                    <p className="text-4xl md:text-5xl font-bold" style={{
                  color: NAVY,
                  fontFamily: "'Playfair Display', Georgia, serif"
                }} data-id="element-1699">
                      40 000 FCFA
                    </p>
                  </div>
                </div>
              </div>}

            {/* Expert — Employeur */}
            {(audience === null || audience === 'employeur') && <div id="parcours-employeur" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow" data-id="element-1700">
                {/* Dark navy header */}
                <div className="p-8 md:p-10" style={{
              backgroundColor: NAVY_DARK
            }} data-id="element-1701">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5 bg-white/20 text-white" data-id="element-1702">
                    Employeur
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{
                fontFamily: "'Playfair Display', Georgia, serif"
              }} data-id="element-1703">
                    Parcours Expert
                  </h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-1704">
                    Pour les employeurs souhaitant accompagner directement le
                    renforcement des capacités de leur personnel de maison.
                  </p>
                </div>

                {/* White content */}
                <div className="p-8 md:p-10" data-id="element-1705">
                  <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{
                color: NAVY_DARK
              }} data-id="element-1706">
                    Le parcours comprend
                  </p>
                  <ol className="space-y-3 mb-7" data-id="element-1707">
                    {['Diagnostic initial à votre domicile', 'Séances de renforcement des capacités individualisées', 'Mise en situation pratique', 'Évaluation finale', "Rapport de progression remis à l'employeur"].map((step, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1708">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs border-2" style={{
                    borderColor: NAVY,
                    color: NAVY
                  }} data-id="element-1709">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed pt-0.5" data-id="element-1710">
                          {step}
                        </span>
                      </li>)}
                  </ol>

                  <div className="rounded-lg p-4 mb-7 border-l-4" style={{
                backgroundColor: '#EEF4FB',
                borderColor: NAVY
              }} data-id="element-1711">
                    <p className="text-sm flex items-start gap-2" style={{
                  color: NAVY
                }} data-id="element-1712">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1713" />
                      <span data-id="element-1714">
                        Attestation de participation PROMAISON délivrée par ADCC
                        au personnel accompagné
                      </span>
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200 text-center" data-id="element-1715">
                    <p className="text-xs tracking-wide mb-2" style={{
                  color: GOLD
                }} data-id="element-1716">
                      Participation aux frais d'encadrement pédagogique
                    </p>
                    <p className="text-4xl md:text-5xl font-bold mb-1" style={{
                  color: NAVY,
                  fontFamily: "'Playfair Display', Georgia, serif"
                }} data-id="element-1717">
                      100 000 FCFA
                    </p>
                    <p className="text-sm italic" style={{
                  color: GOLD
                }} data-id="element-1718">
                      Pris en charge par l'employeur
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* SESSION — date, contact CTAs */}
      {/* ─────────────────────────────────────────────── */}
      <section id="session" className="py-20 md:py-24 relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1719">
        <div className="absolute inset-0 opacity-10" data-id="element-1720">
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(45deg, #D4A574 25%, transparent 25%, transparent 75%, #D4A574 75%)',
          backgroundSize: '40px 40px'
        }} data-id="element-1721" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1722">
          <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
          color: GOLD
        }} data-id="element-1723">
            Rejoindre PROMAISON
          </span>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" style={{
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-1724">
            Prochaine session
          </h2>

          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-6" style={{
          backgroundColor: NAVY_DARK
        }} data-id="element-1725">
            <Calendar className="w-5 h-5" style={{
            color: GOLD
          }} data-id="element-1726" />
            <span className="text-lg font-semibold" style={{
            color: GOLD
          }} data-id="element-1727">
              20 – 30 juin 2026
            </span>
          </div>

          <p className="text-blue-100 mb-12 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-1728">
            Le nombre de participants est volontairement limité afin de garantir
            un accompagnement de qualité.{' '}
            <span className="font-bold text-white" data-id="element-1729">— Places limitées.</span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10 max-w-3xl mx-auto" data-id="element-1730">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-left" data-id="element-1731">
              <p className="font-bold mb-3 text-base" style={{
              color: GOLD
            }} data-id="element-1732">
                Vous êtes personnel de maison ?
              </p>
              <p className="text-blue-100 text-sm leading-relaxed" data-id="element-1733">
                Contactez-nous pour connaître les modalités de participation au
                Parcours Fondamental.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-left" data-id="element-1734">
              <p className="font-bold mb-3 text-base" style={{
              color: GOLD
            }} data-id="element-1735">
                Vous êtes employeur ?
              </p>
              <p className="text-blue-100 text-sm leading-relaxed" data-id="element-1736">
                Contactez-nous pour inscrire votre personnel au Parcours Expert.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-1737">
            <a href="tel:+22901901175555" data-id="element-1738">
              <Button size="lg" className="text-base px-6 py-5 gap-2 shadow-xl bg-white" style={{
              color: NAVY
            }} data-id="element-1739">
                <Phone className="w-4 h-4" style={{
                color: '#E91E63'
              }} data-id="element-1740" />
                +229 01 90 11 75 55
              </Button>
            </a>
            <a href="mailto:contact@adcc.africa?subject=Inscription PROMAISON - Session Juin 2026" data-id="element-1741">
              <Button size="lg" className="text-base px-6 py-5 gap-2 shadow-xl bg-white" style={{
              color: NAVY
            }} data-id="element-1742">
                <Mail className="w-4 h-4" style={{
                color: '#3B6BA0'
              }} data-id="element-1743" />
                contact@adcc.africa
              </Button>
            </a>
          </div>

          <p className="mt-8 text-sm text-blue-200" data-id="element-1744">www.adcc.africa</p>
        </div>

        {/* Inner footer ribbon */}
        <div className="relative mt-20 -mb-20 md:-mb-24 py-6 border-t" style={{
        backgroundColor: NAVY_DARK,
        borderColor: 'rgba(255,255,255,0.1)'
      }} data-id="element-1745">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1746">
            <p className="text-xs md:text-sm text-blue-200" data-id="element-1747">
              <span className="font-bold text-white" data-id="element-1748">PROMAISON par ADCC</span> —
              Association Dignité Compétence Création ·{' '}
              <span className="italic" data-id="element-1749">La dignité par la compétence.</span> · ©
              2026 ADCC — Tous droits réservés
            </p>
          </div>
        </div>
      </section>
    </Layout>;
}